import {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'

import { useTrackingContext } from 'context/trackingContext'
import { setMostPlayed } from 'features/VideoPlayer/MostPlayed'
import { useSelfUpdatingRef } from 'helpers/hooks'

type ProgressData = {
  played: number
  playedSeconds: number
  loaded: number
  loadedSeconds: number
}

type WatchRange = {
  start: number
  end: number
}

type VideoTrackingSession = {
  ownerId?: string
  duration: number
  lastPosition: number | null
  maxPlayedSeconds: number
  totalWatchSeconds: number
  ranges: WatchRange[]
  sent: boolean
  hasPlayedOnce: boolean
  uniqueId: string
  watchSegments: number[]
  isPreview: boolean

}

type Args = {
  viewerUserId?: string
}

const SEGMENT_COUNT = 50

const createUniqueId = () =>
  crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`

const createTrackingSession = (
  ownerId?: string
): VideoTrackingSession => ({
  ownerId,
  duration: 0,
  lastPosition: null,
  maxPlayedSeconds: 0,
  totalWatchSeconds: 0,
  ranges: [],
  sent: false,
  hasPlayedOnce: false,
  uniqueId: createUniqueId(),
  watchSegments: Array(SEGMENT_COUNT).fill(0),
  isPreview: false
})

const mergeRanges = (ranges: WatchRange[]): WatchRange[] => {
  if (!ranges.length) return []

  const sorted = [...ranges]
    .filter(
      range =>
        Number.isFinite(range.start) &&
        Number.isFinite(range.end) &&
        range.end > range.start
    )
    .sort((a, b) => a.start - b.start)

  const merged: WatchRange[] = []

  for (const range of sorted) {
    const last = merged[merged.length - 1]

    if (!last || range.start > last.end) {
      merged.push({ ...range })
      continue
    }

    if (range.end > last.end) {
      last.end = range.end
    }
  }

  return merged
}

const sumRanges = (ranges: WatchRange[]): number => {
  return ranges.reduce(
    (sum, range) => sum + Math.max(0, range.end - range.start),
    0
  )
}

const segmentsEqual = (a: number[], b: number[]): boolean => {
  if (a === b) return true
  if (a.length !== b.length) return false

  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) return false
  }

  return true
}

const normalizeSegments = (
  segments: number[] | undefined
): number[] => {
  const source = Array.isArray(segments)
    ? segments
        .filter(value => Number.isFinite(value))
        .map(value => Math.max(0, Math.floor(value)))
    : []

  if (source.length >= SEGMENT_COUNT) {
    return source.slice(0, SEGMENT_COUNT)
  }

  return [
    ...source,
    ...Array(SEGMENT_COUNT - source.length).fill(0)
  ]
}

export function useMessageVideoAnalytics({
  viewerUserId
}: Args) {
  const {
    addOrEditQueuedPlayedS,
    sendQueuedPlayedS
  } = useTrackingContext()

  const addOrEditQueuedPlayedSRef = useSelfUpdatingRef(
    addOrEditQueuedPlayedS
  )
  const sendQueuedPlayedSRef = useSelfUpdatingRef(sendQueuedPlayedS)

  /*
   * Each message may contain multiple videos.
   *
   * Keep player refs and analytics sessions keyed by video ID so one
   * video's playback cannot overwrite another video's tracking data.
   */
  const videoRefsRef = useRef<Map<string, any>>(new Map())
  const sessionsRef = useRef<Map<string, VideoTrackingSession>>(
    new Map()
  )

  /*
   * UI values represent the most recently active video.
   *
   * The actual analytics values remain stored per video inside
   * sessionsRef.
   */
  const [activeVideoId, setActiveVideoId] = useState<string | null>(
    null
  )
  const [duration, setDuration] = useState<number | null>(null)
  const [progressData, setProgressData] =
    useState<ProgressData>({
      played: 0,
      playedSeconds: 0,
      loaded: 0,
      loadedSeconds: 0
    })
  const [currentSeek, setCurrentSeek] = useState(0)

  const [showReplayGraph, setShowReplayGraph] =
    useState(false)
  const [mostViewedSegments, setMostViewedSegments] =
    useState<number[]>([])

  const hasGraphData = mostViewedSegments.some(value => value > 0)

  const isOwnerView = useCallback(
    (ownerId?: string) => {
      return Boolean(
        viewerUserId &&
          ownerId &&
          viewerUserId === ownerId
      )
    },
    [viewerUserId]
  )

  const shouldTrackVideo = useCallback(
    (videoId?: string, ownerId?: string) => {
      return Boolean(
        videoId &&
          ownerId &&
          viewerUserId &&
          ownerId !== viewerUserId
      )
    },
    [viewerUserId]
  )

const getSession = useCallback(
  (
    videoId: string,
    ownerId?: string,
    isPreview = false
  ): VideoTrackingSession => {
      const existing = sessionsRef.current.get(videoId)

  if (existing) {
      if (ownerId !== undefined) {
          existing.ownerId = ownerId
      }

      existing.isPreview = isPreview

      return existing
  }

  const created = createTrackingSession(ownerId)
  created.isPreview = isPreview

  sessionsRef.current.set(videoId, created)

      return created
    },
    []
  )

  const setVideoRef = useCallback(
    (videoId: string, instance: any | null) => {
      if (instance) {
        videoRefsRef.current.set(videoId, instance)
      } else {
        videoRefsRef.current.delete(videoId)
      }
    },
    []
  )
const getVideoElement = useCallback(
  (videoId: string): HTMLVideoElement | null => {
    return (
      videoRefsRef.current
        .get(videoId)
        ?.getVideoElement?.() ??
      null
    )
  },
  []
)
  const updateWatchSegments = useCallback(
    (
      videoId: string,
      ownerId: string | undefined,
      updater:
        | number[]
        | ((previous: number[]) => number[])
    ) => {
      const session = getSession(videoId, ownerId)
      const previous = session.watchSegments

      const next =
        typeof updater === 'function'
          ? updater(previous)
          : updater

      const normalized = normalizeSegments(next)

      if (!segmentsEqual(previous, normalized)) {
        session.watchSegments = normalized
      }
    },
    [getSession]
  )

  const updatePlayedTracking = useCallback(
    (
      videoId: string,
      ownerId: string | undefined,
      playedSeconds: number
    ) => {
      if (!shouldTrackVideo(videoId, ownerId)) return
      if (!Number.isFinite(playedSeconds)) return
      if (playedSeconds <= 0) return

      const session = getSession(videoId, ownerId)
      const current = Math.max(0, playedSeconds)
      const previous = session.lastPosition

      session.maxPlayedSeconds = Math.max(
        session.maxPlayedSeconds,
        current
      )

      if (previous !== null) {
        const delta = current - previous

        /*
         * Normal forward playback tick.
         *
         * Ignore large forward jumps because those are seeks, not
         * watched time.
         */
        if (delta > 0 && delta <= 5) {
          session.totalWatchSeconds += delta
          session.ranges.push({
            start: previous,
            end: current
          })
        }
      }

      session.lastPosition = current
    },
    [getSession, shouldTrackVideo]
  )

  const queueAndSendPlayed = useCallback(
    (
      videoId?: string,
      ownerId?: string
    ) => {
      if (!videoId) return
      if (!shouldTrackVideo(videoId, ownerId)) return

      const session = sessionsRef.current.get(videoId)

      if (!session) return
      if (session.sent) return

      const videoDuration = Math.max(
        0,
        Number(session.duration || 0)
      )

      const mergedRanges = mergeRanges(session.ranges)
      const uniqueWatchSeconds = sumRanges(mergedRanges)

      const completionRate =
        videoDuration > 0
          ? Math.min(
              100,
              Math.round(
                (uniqueWatchSeconds / videoDuration) * 10000
              ) / 100
            )
          : 0

      const totalWatchSeconds =
        Math.round(session.totalWatchSeconds * 100) / 100

      const roundedUniqueWatchSeconds =
        Math.round(uniqueWatchSeconds * 100) / 100

      /*
       * Do not send an empty playback session.
       */
      if (
        completionRate <= 0 &&
        totalWatchSeconds <= 0 &&
        session.maxPlayedSeconds <= 0
      ) {
        return
      }

      session.sent = true

      addOrEditQueuedPlayedSRef.current({
        video_id: videoId,
        total_watch_seconds: totalWatchSeconds,
        unique_watch_seconds: roundedUniqueWatchSeconds,
        completion_rate: completionRate,
        video_duration_seconds: videoDuration,
        is_preview: session.isPreview
      } as any)

      sendQueuedPlayedSRef.current()
    },
    [
      shouldTrackVideo,
      addOrEditQueuedPlayedSRef,
      sendQueuedPlayedSRef
    ]
  )
const queueAndSendPlayedRef =
  useSelfUpdatingRef(queueAndSendPlayed)
  const flushMostPlayed = useCallback(
    async (
      videoId?: string,
      ownerId?: string
    ) => {
      if (!videoId) return
      if (isOwnerView(ownerId)) return

      const session = sessionsRef.current.get(videoId)

      if (!session) return
      if (!session.hasPlayedOnce) return

      try {
        await setMostPlayed({
          video_id: String(videoId),
          unique_id: session.uniqueId,
          most_played: session.watchSegments
        })
      } catch (error) {
        console.error(
          'setMostPlayed API failed:',
          error
        )
      }
    },
    [isOwnerView]
  )

  const onProgress = useCallback(
    (
      videoId: string,
      ownerId: string | undefined,
      progress: ProgressData
    ) => {
      setActiveVideoId(videoId)
      setProgressData(progress)
      setCurrentSeek(progress.played * 100)

      if (
        Number.isFinite(progress.playedSeconds) &&
        progress.playedSeconds > 0
      ) {
        updatePlayedTracking(
          videoId,
          ownerId,
          progress.playedSeconds
        )
      }
    },
    [updatePlayedTracking]
  )

  const onPlay = useCallback(
    (
      videoId: string,
      ownerId?: string
    ) => {
      const session = getSession(videoId, ownerId)

      session.hasPlayedOnce = true
      setActiveVideoId(videoId)
    },
    [getSession]
  )

  const onPause = useCallback(
    async (
      videoId?: string,
      ownerId?: string
    ) => {
      /*
       * Match PostVideo:
       *
       * Pause only flushes replay-segment tracking.
       * Played/watch-time tracking is sent on ended.
       */
      await flushMostPlayed(videoId, ownerId)
    },
    [flushMostPlayed]
  )

  const onEnded = useCallback(
    async (
      videoId?: string,
      ownerId?: string
    ) => {
      if (!videoId) return

      queueAndSendPlayed(videoId, ownerId)
      await flushMostPlayed(videoId, ownerId)
    },
    [flushMostPlayed, queueAndSendPlayed]
  )

  const onDuration = useCallback(
    (
      videoId: string,
      ownerId: string | undefined,
      videoDuration: number
    ) => {
      const normalizedDuration = Number(videoDuration || 0)
      const session = getSession(videoId, ownerId)

      session.duration =
        Number.isFinite(normalizedDuration) &&
        normalizedDuration > 0
          ? normalizedDuration
          : 0

      if (
        activeVideoId === null ||
        activeVideoId === videoId
      ) {
        setDuration(session.duration)
      }
    },
    [activeVideoId, getSession]
  )

  const onSeek = useCallback(
    async (
      fraction: number,
      videoId?: string,
      ownerId?: string
    ) => {
      if (!videoId) return

      const normalizedFraction =
        fraction > 1 ? fraction / 100 : fraction

      const clampedFraction = Math.max(
        0,
        Math.min(1, normalizedFraction)
      )

      const playerRef = videoRefsRef.current.get(videoId)

      playerRef?.seekTo?.(
        clampedFraction,
        'fraction'
      )

      setActiveVideoId(videoId)
      setCurrentSeek(clampedFraction * 100)

      /*
       * Prevent the seek jump from being counted as watched time.
       * The next progress event becomes the new tracking position.
       */
      const session = getSession(videoId, ownerId)
      session.lastPosition = null

      await flushMostPlayed(videoId, ownerId)
    },
    [flushMostPlayed, getSession]
  )

  const toggleReplayGraph = useCallback(
    (segments?: number[]) => {
      if (Array.isArray(segments)) {
        setMostViewedSegments(
          normalizeSegments(segments)
        )
      }

      setShowReplayGraph(previous => !previous)
    },
    []
  )
useEffect(() => {
  return () => {
    sessionsRef.current.forEach(
      (session, videoId) => {
        queueAndSendPlayedRef.current(
          videoId,
          session.ownerId
        )
      }
    )
  }
}, [queueAndSendPlayedRef])
  return {
    /*
     * UI state for the currently active video.
     */
    duration,
    progressData,
    currentSeek,

    /*
     * Replay graph UI.
     */
    showReplayGraph,
    hasGraphData,
    mostViewedSegments,
    toggleReplayGraph,

    /*
     * Props for each individual HLSPlayer.
     *
     * Message.tsx can keep using:
     *
     * {...hlsProps(item.id, ownerId)}
     */
    hlsProps: (
        videoId: string,
        ownerId?: string,
        isPreview = false
    ) => {
      const session = getSession(
        videoId,
        ownerId,
        isPreview
    )

      return {
        ref: (instance: any | null) => {
          setVideoRef(videoId, instance)
        },

        SEGMENT_COUNT,

        watchSegments: session.watchSegments,

        setWatchSegments: (
          updater:
            | number[]
            | ((previous: number[]) => number[])
        ) => {
          updateWatchSegments(
            videoId,
            ownerId,
            updater
          )
        },

        onPlay: () => {
          onPlay(videoId, ownerId)
        },

        onPause: async () => {
          await onPause(videoId, ownerId)
        },

        onEnded: async () => {
          await onEnded(videoId, ownerId)
        },

        onDuration: (
          videoDuration: number
        ) => {
          onDuration(
            videoId,
            ownerId,
            videoDuration
          )
        },

        onProgress: (
          progress: ProgressData
        ) => {
          onProgress(
            videoId,
            ownerId,
            progress
          )
        }
      }
    },

    /*
     * ProgressBar seek handler.
     */
    onSeek,
    getVideoElement
  }
}